import {
  endpointsCanConnect,
  getCableConnectionEndpoints,
  getMultipolarPoleConnections
} from './cableLogic';

// ==================== COSTANTI PDF CONDIVISE ====================

export const PDF_CONSTANTS = {
  PAGE_WIDTH: 210,        // A4 width in mm
  PAGE_HEIGHT: 297,       // A4 height in mm
  MARGIN: 15,
  PAGE_HEADER_HEIGHT: 12, // Header pagine schematico successive
  CONTENT_START_Y: 20,    // drawPageHeader() riparte da Y=20
  get USABLE_WIDTH() { return this.PAGE_WIDTH - (2 * this.MARGIN); }, // 180mm
  HEADER_HEIGHT: 12,      // Altezza header colorato di un gruppo
  DETAILS_TABLE_HEADER: 12, // Header tabella dettagli ("Dettagli Tecnici Completi")
  STANDARD_CABLE_DETAIL_HEIGHT: 8,   // Altezza dettagli cavo standard (una riga)
  MULTIPOLAR_HEADER_HEIGHT: 8,       // Header cavo multipolare nei dettagli
  MULTIPOLAR_TABLE_HEADER: 8,        // Header tabella poli
  MULTIPOLAR_POLE_HEIGHT: 8,         // Altezza reale per ogni polo nella tabella dettagli
  MULTIPOLAR_SEPARATOR: 2,           // Separatore dopo tabella multipolare
  SVG_PADDING: 4,                    // Padding tra SVG consecutivi
  SVG_BASE_WIDTH: 800,               // Larghezza base degli SVG nel DOM
  GROUP_BOTTOM_SPACING: 8,           // Spazio dopo un gruppo
  // Altezze massime per paginazione
  // NOTA: La prima pagina è riservata ai riepiloghi (cavi/terminali/guaine),
  // quindi i gruppi iniziano SEMPRE dalla seconda pagina.
  // Usare MAX_GROUP_HEIGHT_OTHER_PAGES per la divisione dei gruppi.
  get MAX_GROUP_HEIGHT_FIRST_PAGE() { return this.PAGE_HEIGHT - this.CONTENT_START_Y - this.MARGIN; },  // ~262mm
  get MAX_GROUP_HEIGHT_OTHER_PAGES() { return this.PAGE_HEIGHT - this.CONTENT_START_Y - this.MARGIN; }, // ~262mm
  // Nested/Sheath
  MAX_HEIGHT_NESTED: 220,            // Altezza massima per nodi nested
};

// ==================== FUNZIONI DI SCALA ====================

/**
 * Calcola il fattore di scala per convertire le dimensioni SVG in mm PDF
 */
export const getScale = (usableWidth = PDF_CONSTANTS.USABLE_WIDTH) => {
  return (usableWidth - 6) / PDF_CONSTANTS.SVG_BASE_WIDTH;
};

// ==================== STIME ALTEZZA PER TIPO DI CAVO ====================

const SORT_BY_INDEX = (a, b) => (a?.originalIndex ?? 0) - (b?.originalIndex ?? 0);

const createEmptyBranchState = () => ({ A: [], B: [] });

const dedupeBranchEntries = (entries = []) => {
  const seen = new Set();

  return entries.filter((entry) => {
    const branchCable = entry?.cable;
    if (!branchCable) return false;

    const key = `${branchCable.originalIndex}-${entry.side}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const countBranchNodes = (branchEntries = []) => branchEntries.reduce(
  (total, entry) => total + 1 + countBranchNodes(entry.children || []),
  0
);

const collectConsumedIndexesFromEntries = (entries = [], consumedIndexes = new Set()) => {
  entries.forEach((entry) => {
    if (!entry?.cable) return;
    consumedIndexes.add(entry.cable.originalIndex);
    collectConsumedIndexesFromEntries(entry.children || [], consumedIndexes);
  });

  return consumedIndexes;
};

const buildStandardCableBranches = (anchorCable, candidateCables, settings) => {
  const anchorEndpoints = getCableConnectionEndpoints(anchorCable);
  const branchState = createEmptyBranchState();

  candidateCables.forEach((candidate) => {
    if (!candidate || candidate.originalIndex === anchorCable.originalIndex || (candidate.poli && Array.isArray(candidate.poli))) {
      return;
    }

    const candidateEndpoints = getCableConnectionEndpoints(candidate);

    anchorEndpoints.forEach((anchorEndpoint) => {
      candidateEndpoints.forEach((candidateEndpoint) => {
        if (endpointsCanConnect(anchorEndpoint, candidateEndpoint, settings)) {
          branchState[anchorEndpoint.side].push({
            cable: candidate,
            side: candidateEndpoint.side
          });
        }
      });
    });
  });

  return {
    A: dedupeBranchEntries(branchState.A).sort((left, right) => SORT_BY_INDEX(left.cable, right.cable)),
    B: dedupeBranchEntries(branchState.B).sort((left, right) => SORT_BY_INDEX(left.cable, right.cable))
  };
};

const getBranchedGroupDescriptor = (groupCables, settings, preferredAnchors = []) => {
  const candidates = preferredAnchors.length > 0 ? preferredAnchors : groupCables;

  for (const anchorCable of candidates) {
    const otherCables = groupCables.filter(cable => cable.originalIndex !== anchorCable.originalIndex);
    const branches = buildStandardCableBranches(anchorCable, otherCables, settings);

    if (branches.A.length === otherCables.length) {
      return {
        anchorCable,
        allBranches: branches
      };
    }

    if (branches.B.length === otherCables.length) {
      return {
        anchorCable,
        allBranches: branches
      };
    }
  }

  return null;
};

const buildNestedBranchEntries = ({
  sourceEndpoint,
  candidateCables,
  settings,
  candidateEndpointMap,
  excludedIndexes = new Set()
}) => {
  if (!sourceEndpoint?.connection) {
    return [];
  }

  const getOppositeEndpoint = (candidate, side) => {
    const endpoints = candidateEndpointMap.get(candidate.originalIndex) || [];
    return endpoints.find((endpoint) => endpoint.side !== side) || null;
  };

  const directEntries = candidateCables
    .filter((candidate) =>
      candidate &&
      !excludedIndexes.has(candidate.originalIndex) &&
      !(candidate.poli && Array.isArray(candidate.poli))
    )
    .map((candidate) => {
      const endpoints = candidateEndpointMap.get(candidate.originalIndex) || [];
      const matchingEndpoint = endpoints.find((endpoint) =>
        endpointsCanConnect(sourceEndpoint, endpoint, settings)
      );

      if (!matchingEndpoint) {
        return null;
      }

      const nextExcluded = new Set(excludedIndexes);
      nextExcluded.add(candidate.originalIndex);

      const oppositeEndpoint = getOppositeEndpoint(candidate, matchingEndpoint.side);

      return {
        cable: candidate,
        side: matchingEndpoint.side,
        children: buildNestedBranchEntries({
          sourceEndpoint: oppositeEndpoint,
          candidateCables,
          settings,
          candidateEndpointMap,
          excludedIndexes: nextExcluded
        })
      };
    })
    .filter(Boolean);

  return dedupeBranchEntries(directEntries)
    .sort((left, right) => SORT_BY_INDEX(left.cable, right.cable));
};

const buildSheathBranches = (groupCables, settings, preferredAnchors = []) => {
  const candidates = preferredAnchors.length > 0 ? preferredAnchors : groupCables;
  const candidateEndpointMap = new Map(
    groupCables.map((candidate) => [
      candidate.originalIndex,
      getCableConnectionEndpoints(candidate)
    ])
  );

  for (const anchorCable of candidates) {
    if (!anchorCable) continue;

    const otherCables = groupCables.filter(cable => cable.originalIndex !== anchorCable.originalIndex);
    const anchorEndpoints = getCableConnectionEndpoints(anchorCable);
    const anchorEndpointA = anchorEndpoints.find((endpoint) => endpoint.side === 'A');
    const anchorEndpointB = anchorEndpoints.find((endpoint) => endpoint.side === 'B');

    const excludedIndexes = new Set([anchorCable.originalIndex]);
    const branchesA = buildNestedBranchEntries({
      sourceEndpoint: anchorEndpointA,
      candidateCables: otherCables,
      settings,
      candidateEndpointMap,
      excludedIndexes
    });

    collectConsumedIndexesFromEntries(branchesA, excludedIndexes);

    const branchesB = buildNestedBranchEntries({
      sourceEndpoint: anchorEndpointB,
      candidateCables: otherCables,
      settings,
      candidateEndpointMap,
      excludedIndexes
    });

    const consumedIndexes = collectConsumedIndexesFromEntries(branchesB, excludedIndexes);

    if (consumedIndexes.size === groupCables.length) {
      return {
        anchorCable,
        allBranches: {
          A: branchesA,
          B: branchesB
        }
      };
    }
  }

  return null;
};

const buildMultipolarBranches = (multipolarCable, candidateCables, settings) => {
  const poleConnections = getMultipolarPoleConnections(multipolarCable, candidateCables, settings);
  const branchMap = {};
  const consumedIndexes = new Set();
  const candidateEndpointMap = new Map(
    candidateCables.map((candidate) => [
      candidate.originalIndex,
      getCableConnectionEndpoints(candidate)
    ])
  );

  poleConnections.forEach(({ poloIndex }) => {
    const poleEndpoints = getCableConnectionEndpoints(multipolarCable);
    const poleEndpointA = poleEndpoints.find((endpoint) =>
      endpoint.poloIndex === poloIndex && endpoint.side === 'A'
    );
    const poleEndpointB = poleEndpoints.find((endpoint) =>
      endpoint.poloIndex === poloIndex && endpoint.side === 'B'
    );

    const directA = buildNestedBranchEntries({
      sourceEndpoint: poleEndpointA,
      candidateCables,
      settings,
      candidateEndpointMap,
      excludedIndexes: new Set([multipolarCable.originalIndex])
    });
    const directB = buildNestedBranchEntries({
      sourceEndpoint: poleEndpointB,
      candidateCables,
      settings,
      candidateEndpointMap,
      excludedIndexes: new Set([multipolarCable.originalIndex])
    });

    const branchesA = directA.length > 0 ? directA : [];
    const branchesB = directB.length > 0 ? directB : [];

    if (branchesA.length > 0 || branchesB.length > 0) {
      branchMap[poloIndex] = {
        A: branchesA,
        B: branchesB
      };

      collectConsumedIndexesFromEntries(branchesA, consumedIndexes);
      collectConsumedIndexesFromEntries(branchesB, consumedIndexes);
    }
  });

  return { branchMap, consumedIndexes };
};

/**
 * Stima altezza SVG di un cavo standard (fallback quando il DOM non è disponibile)
 * L'altezza 250 era troppo conservativa. 180 è più realistico per la maggior parte degli SVG standard.
 */
export const estimateStandardSvgHeight = () => {
  return 120;
};

/**
 * Stima altezza SVG di un cavo multipolare
 */
export const estimateMultipolarSvgHeight = (cableOrPoleCount, poleBranches = {}) => {
  if (typeof cableOrPoleCount === 'number') {
    return Math.max(200, 80 + (cableOrPoleCount * 60));
  }

  const cable = cableOrPoleCount;
  const poleCount = cable?.poli?.length || 0;
  const hasAnyBranches = Array.from({ length: poleCount }).some((_, index) => {
    const branches = poleBranches[index] || { A: [], B: [] };
    return branches.A.length > 0 || branches.B.length > 0;
  });
  const visualRowsLeft = Array.from({ length: poleCount }).reduce((total, _, index) => {
    const branches = poleBranches[index] || { A: [], B: [] };
    return total + 1 + countBranchNodes(branches.A);
  }, 0);
  const visualRowsRight = Array.from({ length: poleCount }).reduce((total, _, index) => {
    const branches = poleBranches[index] || { A: [], B: [] };
    return total + 1 + countBranchNodes(branches.B);
  }, 0);
  const visualRows = Math.max(visualRowsLeft, visualRowsRight);
  const compactSvgHeight = Math.max(200, 80 + (poleCount * 60));
  const expandedSvgHeight = Math.max(200, 80 + (visualRows * 60));
  return hasAnyBranches ? expandedSvgHeight : compactSvgHeight;
};

/**
 * Stima altezza SVG di un gruppo guaina (cavi singoli che condividono un SVG)
 * Formula calibrata sull'SVG effettivo di SheathCableVisualization
 */
export const estimateSheathSvgHeight = (cablesOrCableCount, cableBranches = {}) => {
  if (typeof cablesOrCableCount === 'number') {
    return Math.max(160, 40 + (cablesOrCableCount * 50));
  }

  const cables = Array.isArray(cablesOrCableCount) ? cablesOrCableCount : [];
  const cableCount = cables.length;
  const hasAnyBranches = cables.some((cable) => {
    const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
    return branches.A.length > 0 || branches.B.length > 0;
  });
  const visualRowsLeft = cables.reduce((total, cable) => {
    const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
    return total + 1 + countBranchNodes(branches.A);
  }, 0);
  const visualRowsRight = cables.reduce((total, cable) => {
    const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
    return total + 1 + countBranchNodes(branches.B);
  }, 0);
  const visualRows = Math.max(visualRowsLeft, visualRowsRight);
  const compactSvgHeight = Math.max(160, 40 + (cableCount * 50));
  const expandedSvgHeight = Math.max(160, 40 + (visualRows * 50));
  return hasAnyBranches ? expandedSvgHeight : compactSvgHeight;
};

/**
 * Stima altezza SVG di un gruppo di cavi multipli (stessa siglatura, SVG condiviso)
 */
export const estimateMultipleGroupSvgHeight = () => {
  return 250;
};

// ==================== ALTEZZA COMPLETA PER TIPO ====================

/**
 * Calcola altezza totale stimata di un cavo standard (SVG + dettagli)
 */
export const estimateStandardCableHeight = (scale, cable = null) => {
  const svgHeight = estimateStandardSvgHeight();
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING + PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT;
};

/**
 * Calcola altezza totale stimata di un cavo multipolare (SVG + header + tabella poli)
 */
export const estimateMultipolarCableHeight = (cable, scale, poleBranches = {}) => {
  const poleCount = cable.poli?.length || 0;
  const svgHeight = estimateMultipolarSvgHeight(cable, poleBranches);
  const svgScaled = (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  const detailsHeight = PDF_CONSTANTS.MULTIPOLAR_HEADER_HEIGHT +
    PDF_CONSTANTS.MULTIPOLAR_TABLE_HEADER +
    (poleCount * PDF_CONSTANTS.MULTIPOLAR_POLE_HEIGHT) +
    PDF_CONSTANTS.MULTIPOLAR_SEPARATOR;
  return svgScaled + detailsHeight;
};

/**
 * Calcola altezza totale stimata di un gruppo guaina (SVG condiviso + dettagli per ogni cavo)
 */
export const estimateSheathGroupHeight = (cables, scale, cableBranches = {}) => {
  const svgHeight = estimateSheathSvgHeight(cables, cableBranches);
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING + (cables.length * PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT);
};

/**
 * Calcola altezza totale stimata di un gruppo multiplo (SVG condiviso + dettagli per ogni cavo)
 */
export const estimateMultipleGroupHeight = (cables, scale) => {
  const svgHeight = estimateMultipleGroupSvgHeight();
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING + (cables.length * PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT);
};

// ==================== ALTEZZA SOTTO-GRUPPO LOGICO ====================

/**
 * Stima l'altezza di un sotto-gruppo logico (come prodotto da analyzeAndGroupCables).
 * Questo è il sostituto diretto delle formule inline in divideGroupsIntoSubgroups.
 */
export const estimateLogicalSubGroupHeight = (logicalSubGroup, scale) => {
  if (logicalSubGroup.isSingle) {
    const cable = logicalSubGroup.cables[0];
    if (cable.poli && Array.isArray(cable.poli)) {
      return estimateMultipolarCableHeight(cable, scale);
    } else {
      return estimateStandardCableHeight(scale, cable);
    }
  } else {
    // Cavi multipli condividono l'SVG
    return estimateMultipleGroupHeight(logicalSubGroup.cables, scale);
  }
};

const estimateRenderBlockHeight = (renderBlock, scale) => {
  if (renderBlock.type === 'single') {
    return estimateStandardCableHeight(scale, renderBlock.cable);
  }

  if (renderBlock.type === 'multiple') {
    return estimateMultipleGroupHeight(renderBlock.cables, scale);
  }

  if (renderBlock.type === 'multipolar') {
    return estimateMultipolarCableHeight(renderBlock.cable, scale, renderBlock.poleBranches || {});
  }

  if (renderBlock.type === 'sheath') {
    return estimateSheathGroupHeight(renderBlock.cables, scale, renderBlock.cableBranches || {});
  }

  if (renderBlock.type === 'logicalSubGroup') {
    return estimateLogicalSubGroupHeight(renderBlock.logicalSubGroup, scale);
  }

  if (renderBlock.type === 'sheathSingles') {
    return estimateSheathGroupHeight(renderBlock.cables, scale);
  }

  return 0;
};

/**
 * Costruisce blocchi "renderizzabili" coerenti con HybridCableVisualization:
 * - cavi singoli con la stessa guaina condividono un unico SVG
 * - gruppi multipli restano blocchi separati
 * - multipolari restano blocchi separati
 * - singoli senza guaina restano singoli
 */
export const buildRenderableBlocksFromLogicalSubGroups = (logicalSubGroups = [], settings = {}) => {
  const standardCables = logicalSubGroups
    .flatMap(subGroup => subGroup?.cables || [])
    .filter(cable => !(cable?.poli && Array.isArray(cable.poli)))
    .sort(SORT_BY_INDEX);
  const multipolarCables = logicalSubGroups
    .flatMap(subGroup => subGroup?.cables || [])
    .filter(cable => cable?.poli && Array.isArray(cable.poli))
    .sort(SORT_BY_INDEX);

  const renderBlocks = [];
  const sheathGroups = new Map();
  const multipolarBranchMap = new Map();
  const consumedStandardIndexes = new Set();

  multipolarCables.forEach((cable) => {
    const { branchMap, consumedIndexes } = buildMultipolarBranches(cable, standardCables, settings);
    multipolarBranchMap.set(cable.originalIndex, branchMap);
    consumedIndexes.forEach(index => consumedStandardIndexes.add(index));
  });

  const appendToSheathGroup = (cable, branchState = null) => {
    const sheathId = cable?.guaina;
    if (!sheathId) return;

    if (!sheathGroups.has(sheathId)) {
      sheathGroups.set(sheathId, {
        type: 'sheath',
        sheathId,
        cables: [],
        sourceCables: [],
        cableBranches: {},
        sortIndex: cable.originalIndex
      });
    }

    const sheathGroup = sheathGroups.get(sheathId);
    const alreadyPresent = sheathGroup.cables.some(existing => existing.originalIndex === cable.originalIndex);

    if (!alreadyPresent) {
      sheathGroup.cables.push(cable);
      sheathGroup.cables.sort(SORT_BY_INDEX);
      sheathGroup.sortIndex = Math.min(sheathGroup.sortIndex, cable.originalIndex);
    }

    const sourceCables = Array.isArray(branchState?.sourceCables)
      ? branchState.sourceCables
      : [cable];

    sourceCables.forEach((sourceCable) => {
      if (!sourceCable) return;

      const alreadyTracked = sheathGroup.sourceCables.some(
        existing => existing.originalIndex === sourceCable.originalIndex
      );

      if (!alreadyTracked) {
        sheathGroup.sourceCables.push(sourceCable);
      }
    });

    sheathGroup.sourceCables.sort(SORT_BY_INDEX);

    if (branchState && (branchState.A.length > 0 || branchState.B.length > 0)) {
      sheathGroup.cableBranches[cable.originalIndex] = branchState;
    }
  };

  logicalSubGroups.forEach((group) => {
    const remainingCables = (group?.cables || [])
      .filter(cable => !consumedStandardIndexes.has(cable.originalIndex))
      .sort(SORT_BY_INDEX);

    if (remainingCables.length === 0) {
      return;
    }

    if (remainingCables.length === 1) {
      const cable = remainingCables[0];
      if (cable.guaina) {
        appendToSheathGroup(cable);
      } else if (cable.poli && Array.isArray(cable.poli)) {
        renderBlocks.push({
          type: 'multipolar',
          sortIndex: cable.originalIndex,
          cable,
          poleBranches: multipolarBranchMap.get(cable.originalIndex) || {}
        });
      } else {
        renderBlocks.push({
          type: 'single',
          sortIndex: cable.originalIndex,
          cable
        });
      }
      consumedStandardIndexes.add(cable.originalIndex);
      return;
    }

    const sheathAnchors = remainingCables.filter(cable => cable.guaina);
    const preferredAnchors = sheathAnchors.length > 0 ? sheathAnchors : remainingCables;
    const nestedSheathDescriptor = buildSheathBranches(
      remainingCables,
      settings,
      preferredAnchors
    );

    if (nestedSheathDescriptor && nestedSheathDescriptor.anchorCable?.guaina) {
      appendToSheathGroup(nestedSheathDescriptor.anchorCable, {
        ...nestedSheathDescriptor.allBranches,
        sourceCables: remainingCables
      });
      remainingCables.forEach(cable => consumedStandardIndexes.add(cable.originalIndex));
      return;
    }

    const branchedDescriptor = getBranchedGroupDescriptor(
      remainingCables,
      settings,
      preferredAnchors
    );

    if (branchedDescriptor && branchedDescriptor.anchorCable?.guaina) {
      appendToSheathGroup(branchedDescriptor.anchorCable, {
        ...branchedDescriptor.allBranches,
        sourceCables: remainingCables
      });
      remainingCables.forEach(cable => consumedStandardIndexes.add(cable.originalIndex));
      return;
    }

    renderBlocks.push({
      type: 'multiple',
      sortIndex: Math.min(...remainingCables.map(cable => cable.originalIndex)),
      cables: remainingCables
    });
    remainingCables.forEach(cable => consumedStandardIndexes.add(cable.originalIndex));
  });

  multipolarCables.forEach((cable) => {
    if (renderBlocks.some(block => block.type === 'multipolar' && block.cable.originalIndex === cable.originalIndex)) {
      return;
    }

    renderBlocks.push({
      type: 'multipolar',
      sortIndex: cable.originalIndex,
      cable,
      poleBranches: multipolarBranchMap.get(cable.originalIndex) || {}
    });
  });

  sheathGroups.forEach((group) => {
    renderBlocks.push(group);
  });

  return renderBlocks.sort((a, b) => a.sortIndex - b.sortIndex);
};

// ==================== ALTEZZA TOTALE GRUPPO ====================

/**
 * Stima l'altezza totale di un gruppo dai suoi sotto-gruppi logici (output di analyzeAndGroupCables).
 * Sostituisce i calcoli inline duplicati in divideGroupsIntoSubgroups.
 *
 * @param {Array} logicalSubGroups - Output di analyzeAndGroupCables
 * @param {number} scale - Fattore di scala
 * @param {boolean} isSheathGroup - Se true, i cavi singoli condividono un unico SVG (guaina)
 */
export const estimateGroupHeightFromLogicalSubGroups = (logicalSubGroups, scale, isSheathGroup = false, settings = {}) => {
  // HEADER_HEIGHT = header colorato del gruppo
  // DETAILS_TABLE_HEADER = header tabella "Dettagli Tecnici Completi" (appare una volta per gruppo)
  let totalHeight = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;

  const renderBlocks = buildRenderableBlocksFromLogicalSubGroups(logicalSubGroups, settings);
  renderBlocks.forEach(block => {
    totalHeight += estimateRenderBlockHeight(block, scale);
  });

  return totalHeight;
};

// ==================== ALTEZZA RENDERABLE (PER NESTED) ====================

/**
 * Stima l'altezza di un elemento renderizzabile (per la divisione nested/sheath).
 * Sostituisce estimateRenderableHeight in nestedCableStructure.js.
 */
export const estimateRenderableHeight = (renderable, settings = {}, scale = getScale()) => {
  if (renderable.type === 'sheathSingles') {
    return estimateSheathGroupHeight(renderable.cables, scale);
  }

  return estimateRenderBlockHeight(renderable, scale);
};

/**
 * Stima l'altezza di un nodo sheath dai suoi gruppi logici.
 * Sostituisce estimateSheathNodeHeight in nestedCableStructure.js.
 */
export const estimateSheathNodeHeight = (groupList, settings = {}) => {
  const scale = getScale();
  let height = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;

  const renderBlocks = buildRenderableBlocksFromLogicalSubGroups(groupList, settings);
  renderBlocks.forEach((renderBlock) => {
    height += estimateRenderBlockHeight(renderBlock, scale);
  });

  return height;
};

/**
 * Calcola l'altezza di un sotto-gruppo guaina con N cavi singoli.
 * Usata per determinare quanti cavi singoli entrano in una pagina durante la divisione.
 */
export const estimateSheathChunkHeight = (singlesOrCount, scale) => {
  const cables = Array.isArray(singlesOrCount) ? singlesOrCount : [];
  const numSingles = cables.length || Number(singlesOrCount) || 0;
  let height = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;
  if (numSingles > 0) {
    const svgHeight = estimateSheathSvgHeight(cables.length ? cables : numSingles);
    height += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
    height += numSingles * PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT;
  }
  return height;
};

// ==================== LETTURA DAL DOM ====================

/**
 * Legge l'altezza scalata di un SVG dal suo elemento DOM.
 * Restituisce l'altezza in mm per il PDF, tenendo conto della scala.
 */
export const readSVGHeightFromDOM = (svgElement, usableWidth = PDF_CONSTANTS.USABLE_WIDTH) => {
  const viewBox = svgElement.getAttribute('viewBox')?.split(' ');
  if (!viewBox || viewBox.length < 4) return 0;

  const svgWidth = parseFloat(viewBox[2]);
  const svgHeight = parseFloat(viewBox[3]);
  const scale = (usableWidth - 6) / svgWidth;
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
};

/**
 * Calcola l'altezza di un cavo nei dettagli in base ai dati.
 */
export const estimateCableDetailHeight = (cableData) => {
  if (cableData && cableData.poli && Array.isArray(cableData.poli)) {
    return PDF_CONSTANTS.MULTIPOLAR_HEADER_HEIGHT +
      PDF_CONSTANTS.MULTIPOLAR_TABLE_HEADER +
      (cableData.poli.length * PDF_CONSTANTS.MULTIPOLAR_POLE_HEIGHT) +
      PDF_CONSTANTS.MULTIPOLAR_SEPARATOR;
  }
  return PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT;
};

/**
 * Calcola l'altezza precisa di un gruppo leggendo gli SVG dal DOM.
 * Richiede che il container schematico sia renderizzato.
 * Fallback alle stime se il DOM non è disponibile.
 * 
 * @param {Array} cables - Array di cavi nel gruppo
 * @param {Object} settings - Impostazioni dell'app
 * @param {number} usableWidth - Larghezza disponibile in mm (default: PDF_CONSTANTS.USABLE_WIDTH)
 * @param {string|null} groupIdentifier - ID del gruppo nel DOM (opzionale)
 * @returns {number} Altezza stimata del gruppo in mm
 */
export const calculateGroupHeightFromDOM = (cables, settings, usableWidth = PDF_CONSTANTS.USABLE_WIDTH, groupIdentifier = null) => {
  let groupHeight = PDF_CONSTANTS.HEADER_HEIGHT;
  const scale = getScale(usableWidth);

  // Cerca il container schematico nel DOM
  const schematicContainer = document.getElementById('schematic-container');

  if (!schematicContainer) {
    // Fallback alle stime se il DOM non è disponibile
    return estimateGroupHeightFallback(cables, scale);
  }

  // IDENTIFICA IL GRUPPO CORRETTO NEL DOM
  let targetGroup = null;

  if (groupIdentifier) {
    targetGroup = document.getElementById(`cable-group-${groupIdentifier}`);
  }

  if (!targetGroup) {
    // Fallback: cerca il gruppo che contiene i cavi specifici
    const cableGroups = schematicContainer.querySelectorAll('[id^="cable-group-"]');
    const expectedNumbers = cables.map(c => c.originalIndex);
    targetGroup = Array.from(cableGroups).find(group => {
      const cableNumbers = Array.from(group.querySelectorAll('span')).map(span =>
        parseInt(span.textContent?.replace('#', '')) - 1
      );
      return expectedNumbers.every(num => cableNumbers.includes(num));
    });
  }

  if (!targetGroup) {
    // Fallback finale: usa stime
    return estimateGroupHeightFallback(cables, scale);
  }

  // LETTURA DAL DOM: Calcola altezza SVG del gruppo specifico
  const expectedCableNumbers = cables.map(c => c.originalIndex);
  const svgs = targetGroup.querySelectorAll('.bg-gray-50.rounded-lg.p-4 svg, .bg-gray-50.rounded-lg.p-2 svg');
  let totalSvgHeight = 0;

  for (let j = 0; j < svgs.length; j++) {
    // Trova il cavo associato a questo SVG
    const svgContainer = svgs[j].closest('.bg-gray-50.rounded-lg');
    const cableIndexAttr = svgContainer?.getAttribute('data-cable-index');
    const cableIndicesAttr = svgContainer?.getAttribute('data-cable-indices');

    let cableNumbers = [];
    if (cableIndexAttr) {
      cableNumbers = [parseInt(cableIndexAttr)];
    } else if (cableIndicesAttr) {
      cableNumbers = cableIndicesAttr.split(',').map(n => parseInt(n));
    }

    // Calcola solo se almeno uno dei cavi è tra quelli richiesti
    const hasRequiredCable = cableNumbers.some(cableNum => expectedCableNumbers.includes(cableNum));
    if (hasRequiredCable) {
      totalSvgHeight += readSVGHeightFromDOM(svgs[j], usableWidth);
    }
  }
  groupHeight += totalSvgHeight;

  // LETTURA DAL DOM: Calcola altezza dettagli del gruppo specifico
  const detailsSection = targetGroup.querySelector('.bg-blue-50.rounded-lg.p-6');
  const cableDetails = detailsSection ? detailsSection.querySelectorAll('.space-y-2 > div') : [];
  let detailsHeight = 0;

  if (cableDetails.length > 0) {
    detailsHeight = PDF_CONSTANTS.DETAILS_TABLE_HEADER;

    cableDetails.forEach((cableDetailDiv) => {
      const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');
      if (tessera) {
        const cableIndex = parseInt(tessera.querySelector('span')?.textContent?.replace('#', '') - 1);

        if (expectedCableNumbers.includes(cableIndex)) {
          const cableData = cables.find(c => c.originalIndex === cableIndex);
          detailsHeight += estimateCableDetailHeight(cableData);
        }
      }
    });
  }

  groupHeight += detailsHeight;
  return groupHeight;
};

// ==================== FALLBACK STIME ====================

/**
 * Stima l'altezza di un gruppo quando il DOM NON è disponibile.
 * Usata come fallback da calculateGroupHeightFromDOM.
 */
const estimateGroupHeightFallback = (cables, scale) => {
  let groupHeight = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;
  let totalSvgHeight = 0;

  // Raggruppa i cavi per guaina
  const cablesWithSheath = cables.filter(c => c.guaina && !(c.poli && Array.isArray(c.poli)));
  const cablesWithoutSheath = cables.filter(c => !c.guaina && !(c.poli && Array.isArray(c.poli)));
  const multipolarCables = cables.filter(c => c.poli && Array.isArray(c.poli));

  const sheathGroups = {};
  cablesWithSheath.forEach(cable => {
    if (!sheathGroups[cable.guaina]) {
      sheathGroups[cable.guaina] = [];
    }
    sheathGroups[cable.guaina].push(cable);
  });

  // Multipolari
  multipolarCables.forEach(cable => {
    const svgHeight = estimateMultipolarSvgHeight(cable.poli.length);
    totalSvgHeight += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  });

  // Guaine
  Object.values(sheathGroups).forEach(groupCables => {
    const svgHeight = estimateSheathSvgHeight(groupCables.length);
    totalSvgHeight += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  });

  // Standard senza guaina
  cablesWithoutSheath.forEach(() => {
    const svgHeight = estimateStandardSvgHeight();
    totalSvgHeight += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  });

  groupHeight += totalSvgHeight;
  return groupHeight;
};
